/**
 * Collection Adaptive Colors
 * Extracts colors from collection images and applies glassmorphism effects
 */

(function() {
  'use strict';

  // Check for reduced motion preference
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * Run work during browser idle time so canvas.getImageData() does not
   * block collection page first paint / interaction on long lists.
   */
  const scheduleIdle = (typeof window.requestIdleCallback === 'function')
    ? function (callback) {
        return window.requestIdleCallback(callback, { timeout: 2000 });
      }
    : function (callback) {
        return window.setTimeout(function () {
          callback({ didTimeout: false, timeRemaining: function () { return 0; } });
        }, 1);
      };

  /**
   * Extract dominant colors from an image URL
   */
  function extractColorsFromImage(imageUrl, sampleSize = 50) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }

          // Scale down image for faster processing
          const scale = Math.min(sampleSize / img.width, sampleSize / img.height);
          canvas.width = img.width * scale;
          canvas.height = img.height * scale;

          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

          // Sample pixels
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const pixels = imageData.data;
          const colorCounts = new Map();

          // Sample pixels (skip every 16th pixel for performance)
          for (let i = 0; i < pixels.length; i += 16) {
            const r = pixels[i];
            const g = pixels[i + 1];
            const b = pixels[i + 2];
            const a = pixels[i + 3];

            // Skip transparent pixels
            if (a < 128) continue;

            // Quantize colors to reduce noise
            const quantized = quantizeColor(r, g, b);
            const key = quantized.r + ',' + quantized.g + ',' + quantized.b;
            colorCounts.set(key, (colorCounts.get(key) || 0) + 1);
          }

          // Sort colors by frequency
          const sortedColors = Array.from(colorCounts.entries())
            .sort((a, b) => b[1] - a[1])
            .slice(0, 10)
            .map(function(entry) {
              const parts = entry[0].split(',');
              return { r: parseInt(parts[0]), g: parseInt(parts[1]), b: parseInt(parts[2]) };
            });

          // Extract dominant colors
          const primary = sortedColors[0] || { r: 100, g: 100, b: 100 };
          const secondary = sortedColors[1] || primary;
          const accent = sortedColors[2] || secondary;

          // Calculate text color based on luminance
          const textColor = getContrastColor(primary);

          resolve({
            primary: 'rgb(' + primary.r + ', ' + primary.g + ', ' + primary.b + ')',
            secondary: 'rgb(' + secondary.r + ', ' + secondary.g + ', ' + secondary.b + ')',
            accent: 'rgb(' + accent.r + ', ' + accent.g + ', ' + accent.b + ')',
            text: textColor,
            rgba: {
              primary: 'rgba(' + primary.r + ', ' + primary.g + ', ' + primary.b + ', 0.15)',
              secondary: 'rgba(' + secondary.r + ', ' + secondary.g + ', ' + secondary.b + ', 0.1)',
              accent: 'rgba(' + accent.r + ', ' + accent.g + ', ' + accent.b + ', 0.2)'
            }
          });
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = function() {
        reject(new Error('Failed to load image'));
      };

      img.src = imageUrl;
    });
  }

  /**
   * Quantize color to reduce noise
   */
  function quantizeColor(r, g, b, levels = 8) {
    const step = 256 / levels;
    return {
      r: Math.floor(r / step) * step,
      g: Math.floor(g / step) * step,
      b: Math.floor(b / step) * step
    };
  }

  /**
   * Calculate relative luminance and determine text color
   */
  function getContrastColor(rgb) {
    const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
    return luminance > 0.5 ? '#000000' : '#ffffff';
  }

  /**
   * Get cached colors from localStorage
   */
  function getCachedColors(imageUrl) {
    try {
      const cacheKey = 'collection_colors_' + btoa(imageUrl).replace(/[^a-zA-Z0-9]/g, '');
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (e) {
      // Ignore cache errors
    }
    return null;
  }

  /**
   * Cache colors in localStorage
   */
  function cacheColors(imageUrl, colors) {
    try {
      const cacheKey = 'collection_colors_' + btoa(imageUrl).replace(/[^a-zA-Z0-9]/g, '');
      localStorage.setItem(cacheKey, JSON.stringify(colors));
    } catch (e) {
      // Ignore cache errors
    }
  }

  /**
   * Apply colors to collection elements
   */
  function applyColors(colors) {
    const root = document.documentElement;
    
    // Set CSS custom properties
    root.style.setProperty('--collection-primary', colors.primary);
    root.style.setProperty('--collection-secondary', colors.secondary);
    root.style.setProperty('--collection-accent', colors.accent);
    root.style.setProperty('--collection-text', colors.text);
    root.style.setProperty('--collection-glass-primary', colors.rgba.primary);
    root.style.setProperty('--collection-glass-secondary', colors.rgba.secondary);
    root.style.setProperty('--collection-glass-accent', colors.rgba.accent);

    // Apply to specific elements
    const title = document.querySelector('.collection--title');
    if (title) {
      title.style.color = colors.text;
      title.style.transition = prefersReducedMotion ? 'none' : 'color 0.6s ease-out';
    }

    const description = document.querySelector('.collection--description');
    if (description) {
      description.style.color = colors.text === '#ffffff' 
        ? 'rgba(255, 255, 255, 0.95)' 
        : 'rgba(0, 0, 0, 0.85)';
      description.style.transition = prefersReducedMotion ? 'none' : 'color 0.6s ease-out';
    }

    // Add class to indicate colors are loaded
    document.body.classList.add('collection-colors-loaded');
  }

  /**
   * Initialize color extraction for collection page
   */
  function initCollectionColors() {
    const collectionImageContainer = document.querySelector('.collection--image');
    if (!collectionImageContainer) return;

    // Try to get image URL from data attribute first, then from img element
    let imageUrl = collectionImageContainer.getAttribute('data-collection-image');
    
    if (!imageUrl) {
      const collectionImage = collectionImageContainer.querySelector('img');
      if (collectionImage) {
        imageUrl = collectionImage.src || 
                   collectionImage.getAttribute('data-src') || 
                   collectionImage.getAttribute('data-collection-image-src');
      }
    }
    
    if (!imageUrl) return;

    // Check cache first
    const cachedColors = getCachedColors(imageUrl);
    if (cachedColors) {
      applyColors(cachedColors);
      return;
    }

    // Defer the canvas sampling to idle time so first paint is not blocked.
    scheduleIdle(function () {
      extractColorsFromImage(imageUrl)
        .then(function(colors) {
          cacheColors(imageUrl, colors);
          applyColors(colors);
        })
        .catch(function(error) {
          console.warn('Failed to extract collection colors:', error);
          // Apply default colors
          applyColors({
            primary: 'rgb(100, 100, 100)',
            secondary: 'rgb(120, 120, 120)',
            accent: 'rgb(140, 140, 140)',
            text: '#000000',
            rgba: {
              primary: 'rgba(100, 100, 100, 0.15)',
              secondary: 'rgba(120, 120, 120, 0.1)',
              accent: 'rgba(140, 140, 140, 0.2)'
            }
          });
        });
    });
  }

  /**
   * Initialize color extraction for collection list items
   */
  function initCollectionListItems() {
    const items = document.querySelectorAll('.collection__item[data-collection-image]');
    
    items.forEach(function(item) {
      // Try to get image URL from data attribute first, then from img element
      let imageUrl = item.getAttribute('data-collection-image');
      
      if (!imageUrl) {
        const img = item.querySelector('img');
        if (img) {
          imageUrl = img.src || 
                     img.getAttribute('data-src') || 
                     img.getAttribute('data-collection-image-src');
        }
      }
      
      if (!imageUrl) return;

      // Check cache
      const cachedColors = getCachedColors(imageUrl);
      if (cachedColors) {
        applyColorsToItem(item, cachedColors);
        return;
      }

      // Defer every item's canvas sampling to idle time. Each call to
      // extractColorsFromImage does a getImageData loop that is non-trivial
      // for long collection lists; batching them through the idle queue keeps
      // scroll / input latency low.
      scheduleIdle(function () {
        extractColorsFromImage(imageUrl)
          .then(function(colors) {
            cacheColors(imageUrl, colors);
            applyColorsToItem(item, colors);
          })
          .catch(function(error) {
            console.warn('Failed to extract colors for collection item:', error);
          });
      });
    });
  }

  /**
   * Apply colors to a collection list item
   */
  function applyColorsToItem(item, colors) {
    const info = item.querySelector('.collection__item-info');
    if (info) {
      info.style.color = colors.text;
      info.style.textShadow = colors.text === '#ffffff' 
        ? '0 2px 8px rgba(0, 0, 0, 0.8)' 
        : '0 2px 8px rgba(255, 255, 255, 0.8)';
      info.style.transition = prefersReducedMotion ? 'none' : 'color 0.6s ease-out';
    }

    item.setAttribute('data-colors-loaded', 'true');
  }

  /**
   * Initialize parallax effect on collection image
   */
  function initParallax() {
    if (prefersReducedMotion) return;

    const collectionImageContainer = document.querySelector('.collection--image');
    if (!collectionImageContainer) return;

    const collectionImage = collectionImageContainer.querySelector('img');
    if (!collectionImage) return;

    let ticking = false;

    function updateParallax() {
      const rect = collectionImageContainer.getBoundingClientRect();
      const scrolled = window.pageYOffset || document.documentElement.scrollTop;
      const rate = scrolled * 0.3;

      if (rect.bottom >= 0 && rect.top <= window.innerHeight) {
        collectionImage.style.transform = 'translate3d(0, ' + rate + 'px, 0)';
      }

      ticking = false;
    }

    function requestTick() {
      if (!ticking) {
        window.requestAnimationFrame(updateParallax);
        ticking = true;
      }
    }

    window.addEventListener('scroll', requestTick, { passive: true });
  }

  /**
   * Initialize scroll-triggered animations
   */
  function initScrollAnimations() {
    if (prefersReducedMotion) return;

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    // Observe product grid items
    const productItems = document.querySelectorAll('.productgrid--items .productgrid--item');
    productItems.forEach(function(item, index) {
      item.style.opacity = '0';
      item.style.transform = 'translateY(20px)';
      item.style.transition = 'opacity 0.6s ease-out ' + (index * 0.05) + 's, transform 0.6s ease-out ' + (index * 0.05) + 's';
      observer.observe(item);
    });

    // Add visible class handler
    const style = document.createElement('style');
    style.textContent = `
      .productgrid--item.is-visible {
        opacity: 1 !important;
        transform: translateY(0) !important;
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Initialize everything when DOM is ready
   */
  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
      return;
    }

    // Initialize collection page colors
    if (document.querySelector('.collection--image')) {
      initCollectionColors();
      initParallax();
      initScrollAnimations();
    }

    // Initialize collection list items
    if (document.querySelector('.collection-list__content')) {
      initCollectionListItems();
    }
  }

  // Start initialization
  init();

})();

